import _ from 'lodash';
import {
  ActionType,
  SendMessageActionFormData,
  SIMPLE_NOTIFICATIONS_ALERT_EVENT_BC,
  SimplifiedWorkflowActionStepFormData,
  SimplifiedWorkflowStepFormData,
  SimplifiedWorkflowTriggerStepFormData,
  StepType,
} from '../data/models/simplified-workflow.model';
import { SmartLabel } from '../data/models/smart-label.model';
import { SystemEvent } from '../data/models/system-event.model';
import {
  WorkflowBaseDateTimeRef,
  WorkflowCaseClause,
  WorkflowConditionAction,
  WorkflowConditionalBC,
  WorkflowConditionOutcomeStatus,
  WorkflowElseCase,
  WorkflowIntermediateStep,
  WorkflowPublishAlertAction,
  WorkflowStartStep,
  WorkflowStep,
  WorkflowStepType,
  WorkflowWaitAction,
} from '../data/models/workflow.model';
import {
  WaitActionTimePeriod,
  WaitAmountUnit,
} from '../data/workflow-form.model';
import simplifiedWorkflowUtils from './simplifiedWorkflowUtils';
import workflowUtils from './workflowUtils';

const sendMessageBranchStepName = 'SendMessageBranch';

export class TripLegPickupMessageTransformer implements SimplifiedWorkflowFormTransformer {
  canTransform(steps: SimplifiedWorkflowStepFormData[]): boolean {
    if (steps.length === 2) {
      const firstStep = steps[0];
      if (firstStep.type !== StepType.Trigger) {
        return false;
      }
      const triggerStep = steps[0] as SimplifiedWorkflowTriggerStepFormData;
      if (
        triggerStep.event === WorkflowBaseDateTimeRef.TripLegPickup &&
        triggerStep.wait?.waitTimePeriod === WaitActionTimePeriod.Before
      ) {
        const secondStep = steps[1];
        if (
          secondStep.type === StepType.Action &&
          (secondStep as SimplifiedWorkflowActionStepFormData).actionType ===
            ActionType.SendMessage
        ) {
          return true;
        }
      }
    }
    return false;
  }
  transform(
    steps: SimplifiedWorkflowStepFormData[],
    smartLabels: SmartLabel[],
    systemEvents: SystemEvent[],
  ): WorkflowStep[] {
    const triggerStep = steps[0] as SimplifiedWorkflowTriggerStepFormData;
    const sendMessageStep = steps[1] as SendMessageActionFormData;
    const waitAmount = parseInt(triggerStep.wait?.waitAmount ?? '0');
    let waitAmountUnit =
      triggerStep.wait?.waitAmountUnit ?? WaitAmountUnit.Minutes;
    const minutes = workflowUtils.waitAmountToMinutes(
      waitAmount,
      waitAmountUnit,
    );
    const waitAction: WorkflowWaitAction = {
      wait: {
        baseDateTimeRef: WorkflowBaseDateTimeRef.TripLegPickup,
        deltaMinutes: minutes * -1,
      },
    };
    const pubAlertAction: WorkflowPublishAlertAction = {
      publishAlert: {
        alertEventBC: SIMPLE_NOTIFICATIONS_ALERT_EVENT_BC,
        alertDescription:
          simplifiedWorkflowUtils.replaceSmartLabelsWithBC(
            sendMessageStep.message ?? '',
            smartLabels,
          ) ?? '',
        alertTypeBC: sendMessageStep.messageType ?? '',
        alertRecipientBC: sendMessageStep.recipient ?? '',
        interactiveResponse: false,
      },
    };
    const publishAlertStepName = 'SendMessage';
    const startStep: WorkflowStartStep = {
      name: 'StartOnTripLegOpen',
      description:
        'Start when trip leg opened and wait until x time before trip leg pickup',
      type: WorkflowStepType.Start,
      trigger: {
        events: simplifiedWorkflowUtils.getVersionedSystemEvents(
          ['TripLegOpened'],
          systemEvents,
        ),
      },
      action: waitAction,
      onSuccess: {
        triggerStep: publishAlertStepName,
      },
    };
    const resetOnUpdateStep: WorkflowIntermediateStep = {
      name: 'ResetOnUpdate',
      description: 'Reset wait timer if trip leg updated',
      type: WorkflowStepType.Intermediate,
      trigger: {
        events: simplifiedWorkflowUtils.getVersionedSystemEvents(
          [
            'OpenTripLegUpdated',
            'OfferedTripLegUpdated',
            'AcceptedTripLegUpdated',
            'ConfirmedTripLegUpdated',
          ],
          systemEvents,
        ),
      },
      action: waitAction,
      onSuccess: {
        triggerStep: publishAlertStepName,
      },
    };
    const publishAlertStep: WorkflowIntermediateStep = {
      name: publishAlertStepName,
      description: 'Send message before trip leg pickup',
      type: WorkflowStepType.Intermediate,
      action: pubAlertAction,
    };
    const cancelStep: WorkflowIntermediateStep = {
      name: 'CancelWorkflow',
      description: 'Cancel workflow if trip leg canceled or completed',
      type: WorkflowStepType.Intermediate,
      trigger: {
        events: simplifiedWorkflowUtils.getVersionedSystemEvents(
          ['TripLegCanceled', 'TripLegCanceledLate', 'TripLegCompleted'],
          systemEvents,
        ),
      },
      action: {
        cancel: {},
      },
    };

    return [startStep, resetOnUpdateStep, publishAlertStep, cancelStep];
  }
}

export class SendMessageTransformer implements SimplifiedWorkflowFormTransformer {
  canTransform(steps: SimplifiedWorkflowStepFormData[]): boolean {
    if (steps.length === 2) {
      if (steps[0].type === StepType.Trigger) {
        const secondStep = steps[1];
        if (
          secondStep.type === StepType.Action &&
          (secondStep as SimplifiedWorkflowActionStepFormData).actionType ===
            ActionType.SendMessage
        ) {
          return true;
        }
      }
    }
    return false;
  }

  transform(
    steps: SimplifiedWorkflowStepFormData[],
    smartLabels: SmartLabel[],
    systemEvents: SystemEvent[],
  ): WorkflowStep[] {
    return getSendMessageSteps(steps, smartLabels, systemEvents);
  }
}

export class SendInteractiveResponseMessageTransformer implements SimplifiedWorkflowFormTransformer {
  canTransform(steps: SimplifiedWorkflowStepFormData[]): boolean {
    if (steps.length > 2) {
      if (steps[0].type === StepType.Trigger) {
        const secondStep = steps[1];
        if (
          secondStep.type === StepType.Action &&
          (secondStep as SimplifiedWorkflowActionStepFormData).actionType ===
            ActionType.SendMessage &&
          (secondStep as SendMessageActionFormData).interactiveResponse === true
        ) {
          return true;
        }
      }
    }
    return false;
  }

  transform(
    steps: SimplifiedWorkflowStepFormData[],
    smartLabels: SmartLabel[],
    systemEvents: SystemEvent[],
  ): WorkflowStep[] {
    const messageSteps = getSendMessageSteps(steps, smartLabels, systemEvents);
    messageSteps[messageSteps.length - 1].onSuccess = {
      triggerStep: sendMessageBranchStepName,
    };
    const timeoutStep = getTimeoutStep(steps[1], steps.slice(2));
    if (timeoutStep) {
      messageSteps[messageSteps.length - 1].onFailure = {
        triggerStep: getBranchedStepName(
          sendMessageBranchStepName,
          timeoutStep.branch ?? '',
        ),
      };
    }

    const branchingStep = getBranchingStep(steps[1], steps.slice(2));
    if (branchingStep) {
      const descendantSteps = getBranchedDescendantSteps(
        steps[1],
        steps.slice(2),
        smartLabels,
      );
      return [...messageSteps, branchingStep, ...descendantSteps];
    }
    return messageSteps;
  }
}

export class SimplifiedWorkflowFormTransformerRegistry {
  private transformers: SimplifiedWorkflowFormTransformer[] = [
    new TripLegPickupMessageTransformer(),
    new SendInteractiveResponseMessageTransformer(),
    new SendMessageTransformer(),
  ];

  getTransformer(
    steps: SimplifiedWorkflowStepFormData[],
  ): SimplifiedWorkflowFormTransformer {
    const transformer = this.transformers.find((t) => t.canTransform(steps));
    if (!transformer) {
      throw new Error(`No transformer available for workflow`);
    }
    return transformer;
  }
}

export interface SimplifiedWorkflowFormTransformer {
  canTransform(steps: SimplifiedWorkflowStepFormData[]): boolean;
  transform(
    steps: SimplifiedWorkflowStepFormData[],
    smartLabels: SmartLabel[],
    systemEvents: SystemEvent[],
  ): WorkflowStep[];
}

function getSendMessageSteps(
  steps: SimplifiedWorkflowStepFormData[],
  smartLabels: SmartLabel[],
  systemEvents: SystemEvent[],
): WorkflowStep[] {
  const triggerStep = steps[0] as SimplifiedWorkflowTriggerStepFormData;
  const sendMessageStep = steps[1] as SendMessageActionFormData;

  const pubAlertAction: WorkflowPublishAlertAction = {
    publishAlert: {
      alertEventBC: SIMPLE_NOTIFICATIONS_ALERT_EVENT_BC,
      alertDescription:
        simplifiedWorkflowUtils.replaceSmartLabelsWithBC(
          sendMessageStep.message ?? '',
          smartLabels,
        ) ?? '',
      alertTypeBC: sendMessageStep.messageType ?? '',
      alertRecipientBC: sendMessageStep.recipient ?? '',
      interactiveResponse: sendMessageStep?.interactiveResponse || false,
      responseTimeoutMinutes: toInt(sendMessageStep?.responseTimeoutMinutes),
      sendReminderIfNoResponse:
        sendMessageStep?.sendReminderIfNoResponse || false,
      numberOfReminders: toInt(sendMessageStep?.numberOfReminders),
      sendReminderAfterMinutes: toInt(
        sendMessageStep?.sendReminderAfterMinutes,
      ),
      alertReminder: sendMessageStep?.alertReminder,
      alertForSTOReservations:
        sendMessageStep?.alertForSTOReservations || false,
    },
  };

  let waitAmount = 0;
  if (triggerStep.wait?.waitAmount) {
    waitAmount = parseInt(triggerStep.wait?.waitAmount);
  }
  if (waitAmount === 0) {
    const startStep: WorkflowStartStep = {
      name: 'SendMessage',
      description: 'Send message on event',
      type: WorkflowStepType.Start,
      trigger: {
        events: [
          {
            name: triggerStep.event ?? '',
            version: simplifiedWorkflowUtils.getSystemEventMostRecentVersion(
              triggerStep.event ?? '',
              systemEvents,
            ),
          },
        ],
      },
      action: pubAlertAction,
    };
    return [startStep];
  } else {
    let waitAmountUnit =
      triggerStep.wait?.waitAmountUnit ?? WaitAmountUnit.Minutes;
    const minutes = workflowUtils.waitAmountToMinutes(
      waitAmount,
      waitAmountUnit,
    );
    const waitAction: WorkflowWaitAction = {
      wait: {
        baseDateTimeRef: WorkflowBaseDateTimeRef.CurrentTime,
        deltaMinutes: minutes,
      },
    };
    const secondStepName = 'SendMessage';
    const startStep: WorkflowStartStep = {
      name: 'Wait',
      description: 'On event, wait before sending message',
      type: WorkflowStepType.Start,
      trigger: {
        events: [
          {
            name: triggerStep.event ?? '',
            version: simplifiedWorkflowUtils.getSystemEventMostRecentVersion(
              triggerStep.event ?? '',
              systemEvents,
            ),
          },
        ],
      },
      action: waitAction,
      onSuccess: {
        triggerStep: secondStepName,
      },
    };
    const secondStep: WorkflowIntermediateStep = {
      name: secondStepName,
      description: 'After waiting, send message',
      type: WorkflowStepType.Intermediate,
      action: pubAlertAction,
    };
    return [startStep, secondStep];
  }
}

function getTimeoutStep(
  sendMessageStep: SendMessageActionFormData,
  otherSteps: SimplifiedWorkflowStepFormData[],
): SimplifiedWorkflowStepFormData | undefined {
  const children = otherSteps.filter(
    (step) => step.parentId === sendMessageStep.tempId,
  );
  if (children.length === 0) {
    return undefined;
  }
  const timeoutStep = children.find(
    (child) =>
      child.branch ===
      workflowUtils.getConditionalName(WorkflowConditionalBC.Timeout),
  );
  return timeoutStep;
}

function getBranchingStep(
  sendMessageStep: SendMessageActionFormData,
  otherSteps: SimplifiedWorkflowStepFormData[],
): WorkflowIntermediateStep | undefined {
  const name = sendMessageBranchStepName;
  const description = 'Branch step for interactive messages';
  const children = otherSteps.filter(
    (step) => step.parentId === sendMessageStep.tempId,
  );
  if (children.length === 0) {
    return undefined;
  }
  const affirmStep = children.find(
    (child) =>
      child.branch ===
      workflowUtils.getConditionalName(WorkflowConditionalBC.Affirm),
  );
  const negateStep = children.find(
    (child) =>
      child.branch ===
      workflowUtils.getConditionalName(WorkflowConditionalBC.Negate),
  );
  if (!affirmStep || !negateStep) {
    return undefined;
  }
  const affirmClause: WorkflowCaseClause = {
    when: {
      op: 'eq',
      value: workflowUtils.getConditionalBC(affirmStep.branch!)!,
    },
    outcomeStatus: WorkflowConditionOutcomeStatus.Succeeded,
  };
  const negateCase: WorkflowElseCase = {
    outcomeStatus: WorkflowConditionOutcomeStatus.Failed,
  };
  const conditionAction: WorkflowConditionAction = {
    condition: {
      inputPath: '$.detail.messageResponseBC',
      cases: [affirmClause],
      elseCase: negateCase,
    },
  };
  return {
    name,
    description,
    type: WorkflowStepType.Intermediate,
    action: conditionAction,
    onSuccess: {
      triggerStep: getBranchedStepName(name, affirmStep.branch ?? ''),
    },
    onFailure: {
      triggerStep: getBranchedStepName(name, negateStep.branch ?? ''),
    },
  };
}

function getBranchedDescendantSteps(
  parentStep: SimplifiedWorkflowStepFormData,
  descendants: SimplifiedWorkflowStepFormData[],
  smartLabels: SmartLabel[],
): WorkflowIntermediateStep[] {
  const children = descendants.filter(
    (step) => step.parentId === parentStep.tempId,
  );
  return children
    .map((step) =>
      getDescendantSteps(
        step,
        getBranchedStepName(sendMessageBranchStepName, step.branch ?? ''),
        descendants,
        smartLabels,
      ),
    )
    .flat();
}

function getDescendantSteps(
  step: SimplifiedWorkflowActionStepFormData,
  stepName: string,
  otherSteps: SimplifiedWorkflowActionStepFormData[],
  smartLabels: SmartLabel[],
  index = 1,
): WorkflowIntermediateStep[] {
  const child = otherSteps.find((s) => s.parentId === step.tempId);
  const nextStepName = child ? `${stepName}-${index}` : undefined;
  const workflowStep = getStep(step, stepName, smartLabels, nextStepName);
  if (!workflowStep) {
    return [];
  }
  if (child) {
    return [
      workflowStep,
      ...getDescendantSteps(
        child,
        `${workflowStep.name}-${index}`,
        otherSteps,
        smartLabels,
        index + 1,
      ),
    ];
  }
  return [workflowStep];
}

function getStep(
  step: SimplifiedWorkflowActionStepFormData,
  stepName: string,
  smartLabels: SmartLabel[],
  nextStepName?: string,
) {
  switch (step.actionType) {
    case ActionType.ActivateWillCall:
      return getActivateWillCallStep(stepName, nextStepName);
    case ActionType.CancelReturnTrip:
      return getCancelTripLegStep(stepName, nextStepName);
    case ActionType.SendMessage:
      return getSendMessageStep(step, stepName, smartLabels, nextStepName);
  }
  return undefined;
}

function getActivateWillCallStep(
  stepName: string,
  nextStepName?: string,
): WorkflowIntermediateStep {
  const step: WorkflowIntermediateStep = {
    name: stepName,
    description: 'Activate Will Call',
    type: WorkflowStepType.Intermediate,
    action: {
      activateWillCall: {},
    },
  };
  if (nextStepName) {
    step.onSuccess = {
      triggerStep: nextStepName,
    };
  }
  return step;
}

function getCancelTripLegStep(
  stepName: string,
  nextStepName?: string,
): WorkflowIntermediateStep {
  const step: WorkflowIntermediateStep = {
    name: stepName,
    description: 'Cancel Trip Leg',
    type: WorkflowStepType.Intermediate,
    action: {
      cancelTripLeg: {
        tripLegStatusReasonBCs: ['WORKFLOWCANCEL'],
      },
    },
  };
  if (nextStepName) {
    step.onSuccess = {
      triggerStep: nextStepName,
    };
  }
  return step;
}

function getSendMessageStep(
  step: SendMessageActionFormData,
  stepName: string,
  smartLabels: SmartLabel[],
  nextStepName?: string,
): WorkflowIntermediateStep {
  const pubAlertAction: WorkflowPublishAlertAction = {
    publishAlert: {
      alertEventBC: SIMPLE_NOTIFICATIONS_ALERT_EVENT_BC,
      alertDescription:
        simplifiedWorkflowUtils.replaceSmartLabelsWithBC(
          step.message ?? '',
          smartLabels,
        ) ?? '',
      alertTypeBC: step.messageType ?? '',
      alertRecipientBC: step.recipient ?? '',
      alertForSTOReservations: step?.alertForSTOReservations || false,
      interactiveResponse: false,
    },
  };
  const stepToReturn: WorkflowIntermediateStep = {
    name: stepName,
    description: 'Send Message',
    type: WorkflowStepType.Intermediate,
    action: pubAlertAction,
  };
  if (nextStepName) {
    stepToReturn.onSuccess = {
      triggerStep: nextStepName,
    };
  }
  return stepToReturn;
}

function getBranchedStepName(stepName: string, branch: string) {
  return `${stepName}-${branch}`;
}

function toInt(value?: string) {
  if (value) {
    return parseInt(value);
  }
  return undefined;
}
